/**
 * Outbound deliveries — the live view of the outbox.
 *
 * Each row = one external side-effect (HTTP request, connector push,
 * future kinds). The drain worker advances the status machine in the
 * background; this page lets ops/admins:
 *
 *   - See what's queued, in flight, succeeded, dead-lettered
 *   - Drill into a single delivery (payload, headers, last response,
 *     full error)
 *   - Retry manually (resets attempts, schedules for next tick)
 *   - Cancel manually (skips remaining retries → dead_letter)
 *
 * Live updates via SSE: the page subscribes to ``delivery_updated``
 * events from the broker and refetches the matching row, so a row
 * status change appears in the table without a manual refresh.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  ExternalLink,
  GitBranch,
  Hand,
  Loader2,
  Package,
  PlayCircle,
  RefreshCw,
  Send,
  X,
  Zap,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  EmptyState,
  PageContainer,
  PageContent,
  PageHeader,
} from "@/components/ui/page";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  deliveriesApi,
  type DeliveryBranchSnapshot,
  type DeliveryStatus,
  type DeliverySummary,
  type SubflowFire,
} from "@/lib/api-deliveries";
import {
  canFireBranch,
  outcomeTone,
} from "@/components/deliveries/deliverySubflowView";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";


// Exactly 4 states match the backend drain worker — see
// DeliveryStatus type in api-deliveries.ts for the rationale.
const STATUSES: { value: DeliveryStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "in_flight", label: "In flight" },
  { value: "succeeded", label: "Succeeded" },
  { value: "dead_letter", label: "Dead letter" },
];


function StatusBadge({ status }: { status: DeliveryStatus }) {
  const tone =
    status === "succeeded"
      ? "active"
      : status === "dead_letter"
        ? "danger"
        : "neutral";
  const Icon =
    status === "succeeded"
      ? CheckCircle2
      : status === "dead_letter"
        ? AlertCircle
        : status === "in_flight"
          ? Loader2
          : CircleDashed;
  return (
    <Badge tone={tone} className="gap-1">
      <Icon
        className={cn(
          "h-3 w-3",
          status === "in_flight" && "animate-spin",
        )}
        strokeWidth={1.5}
      />
      {status.replace("_", " ")}
    </Badge>
  );
}


function fmtRelative(iso: string): string {
  const d = new Date(iso);
  const ago = Date.now() - d.getTime();
  if (ago < 0) {
    // Future timestamp (next_attempt_at) — show absolute time.
    return d.toLocaleTimeString();
  }
  if (ago < 60_000) return "just now";
  if (ago < 3600_000) return `${Math.floor(ago / 60_000)}m ago`;
  if (ago < 86_400_000) return `${Math.floor(ago / 3600_000)}h ago`;
  return d.toLocaleDateString();
}


export function DeliveriesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canExecute = can("outbound", "execute");
  const canExecuteAction = can("outbound", "execute_action");

  const [statusFilter, setStatusFilter] = React.useState<DeliveryStatus | "">("");
  const [targetFilter, setTargetFilter] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ["deliveries", { statusFilter, targetFilter }],
    queryFn: () =>
      deliveriesApi.list({
        status: statusFilter || undefined,
        target_key: targetFilter.trim() || undefined,
        limit: 100,
      }),
    refetchInterval: 5_000, // simple polling — paired with SSE if available later
  });

  const rows = listQ.data?.items ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("deliveries.title", "Deliveries")}
        description={t(
          "deliveries.subtitle",
          "Every outbound side-effect (HTTP, connector, …) goes through here. The drain worker advances rows in the background; you can inspect, retry, or cancel manually.",
        )}
      />
      <PageContent>
        {/* Filter bar */}
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-surface p-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Filter
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as DeliveryStatus | "")}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <input
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
            placeholder="target contains…"
            className="h-8 w-56 rounded-md border border-border bg-background px-2 text-xs"
          />
          {(statusFilter || targetFilter) && (
            <button
              onClick={() => {
                setStatusFilter("");
                setTargetFilter("");
              }}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
          <div className="ml-auto text-[11px] text-muted-foreground">
            {listQ.data?.total ?? 0} total
          </div>
        </div>

        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Send}
            title={t("deliveries.empty", "No deliveries yet")}
            description={t(
              "deliveries.emptyDescription",
              "Outbound sinks (http_sink, connector_sink) populate this list. Trigger an outbound workflow to see deliveries appear here.",
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-32">Status</Th>
                  <Th>Target</Th>
                  <Th className="w-24">Kind</Th>
                  <Th className="w-20">Attempts</Th>
                  <Th>Last error</Th>
                  <Th className="w-32">Created</Th>
                  <Th className="w-32 text-right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((d) => (
                  <Tr
                    key={d.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedId(d.id)}
                  >
                    <Td onClick={(e) => e.stopPropagation()}>
                      <StatusBadge status={d.status} />
                    </Td>
                    <Td className="font-mono text-xs">{d.target_key}</Td>
                    <Td className="font-mono text-[10px] text-muted-foreground">
                      {d.sink_kind}
                    </Td>
                    <Td className="text-xs">
                      {d.attempts}/{d.max_attempts}
                    </Td>
                    <Td className="max-w-[24rem] truncate text-xs text-muted-foreground">
                      {d.last_error ?? ""}
                    </Td>
                    <Td className="text-xs text-muted-foreground">
                      {fmtRelative(d.created_at)}
                    </Td>
                    <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                      {canExecute && (
                        <DeliveryRowActions
                          delivery={d}
                          onChanged={() =>
                            qc.invalidateQueries({ queryKey: ["deliveries"] })
                          }
                        />
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      <DeliveryDrawer
        deliveryId={selectedId}
        onClose={() => setSelectedId(null)}
        canExecute={canExecute}
        canExecuteAction={canExecuteAction}
      />
    </PageContainer>
  );
}


// ---------------------------------------------------------------------------
// Row-level inline actions (Retry / Cancel quick buttons)
// ---------------------------------------------------------------------------


function DeliveryRowActions({
  delivery,
  onChanged,
}: {
  delivery: DeliverySummary;
  onChanged: () => void;
}) {
  const retry = useMutation({
    mutationFn: () => deliveriesApi.retry(delivery.id),
    onSuccess: onChanged,
  });
  const cancel = useMutation({
    mutationFn: () => deliveriesApi.cancel(delivery.id),
    onSuccess: onChanged,
  });

  const canRetry =
    delivery.status === "dead_letter" ||
    delivery.status === "succeeded"; // re-fire even after success if needed
  const canCancel =
    delivery.status === "pending" || delivery.status === "in_flight";

  return (
    <div className="flex items-center justify-end gap-1">
      {canRetry && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Retry now"
          title="Retry now (reset attempts, schedule immediate)"
          disabled={retry.isPending}
          onClick={() => retry.mutate()}
        >
          <RefreshCw
            className={cn("h-3.5 w-3.5", retry.isPending && "animate-spin")}
            strokeWidth={1.5}
          />
        </Button>
      )}
      {canCancel && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Cancel"
          title="Cancel (mark dead-letter, skip retries)"
          disabled={cancel.isPending}
          onClick={() => {
            if (confirm("Cancel this delivery? It will be marked as dead-letter.")) {
              cancel.mutate();
            }
          }}
        >
          <X className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
        </Button>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Side drawer with full delivery detail
// ---------------------------------------------------------------------------


function DeliveryDrawer({
  deliveryId,
  onClose,
  canExecute,
  canExecuteAction,
}: {
  deliveryId: string | null;
  onClose: () => void;
  canExecute: boolean;
  canExecuteAction: boolean;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ["deliveries", "detail", deliveryId],
    queryFn: () => deliveriesApi.get(deliveryId!),
    enabled: !!deliveryId,
    refetchInterval: 3_000,
  });

  const retry = useMutation({
    mutationFn: () => deliveriesApi.retry(deliveryId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deliveries"] });
    },
  });
  const cancel = useMutation({
    mutationFn: () => deliveriesApi.cancel(deliveryId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deliveries"] });
    },
  });
  // ADR-0008 PR3b2 — manual sub-flow trigger from the drawer. The
  // branch must be declared on the source sink AND have ``trigger
  // ∈ {manual, both}`` AND a non-null linked workflow — backend
  // enforces all three (errors surfaced inline as a toast/banner via
  // ``mutation.error``). Each click appends to subflow_fires; the
  // detail query is auto-refetched on success so the timeline updates.
  const [actionError, setActionError] = React.useState<string | null>(null);
  const executeAction = useMutation({
    mutationFn: (branchId: string) =>
      deliveriesApi.executeAction(deliveryId!, branchId),
    onSuccess: () => {
      setActionError(null);
      qc.invalidateQueries({ queryKey: ["deliveries", "detail", deliveryId] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const d = detailQ.data;

  return (
    <Sheet open={!!deliveryId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full max-w-[40rem] overflow-y-auto">
        {!d ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {detailQ.isLoading ? "Loading…" : "Delivery not found"}
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-2">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <h2 className="text-sm font-semibold">Delivery</h2>
                <StatusBadge status={d.status} />
                {/* The delivery is the classic "journalist" anchor for
                    a trace — clicking here pivots to the causal graph
                    walking backwards through execution → message →
                    ingress. Always visible (read-only action). */}
                <Button
                  asChild
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-7 gap-1.5 text-xs"
                >
                  <Link to={`/trace/delivery/${d.id}`}>
                    <GitBranch className="h-3.5 w-3.5" strokeWidth={1.5} />
                    View trace
                  </Link>
                </Button>
              </div>
              <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                {d.id}
              </div>
            </div>

            {/* Meta grid */}
            <DetailGrid
              rows={[
                ["Sink kind", d.sink_kind],
                ["Target key", d.target_key],
                ["Attempts", `${d.attempts} / ${d.max_attempts}`],
                ["Next attempt", new Date(d.next_attempt_at).toLocaleString()],
                ["Created", new Date(d.created_at).toLocaleString()],
                d.completed_at
                  ? ["Completed", new Date(d.completed_at).toLocaleString()]
                  : null,
                d.source_execution_id
                  ? [
                      "Source execution",
                      <span className="font-mono text-[10px]">
                        {d.source_execution_id}
                      </span>,
                    ]
                  : null,
              ]}
            />

            {/* Last error */}
            {d.last_error && (
              <Alert tone="danger" title="Last error">
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs">
                  {d.last_error}
                </pre>
              </Alert>
            )}

            {/* ADR-0008 PR3 — outcome chip + sub-flow timeline + manual
                action buttons. Always rendered (even pre-classification)
                so the operator can see "what's wired" before the drain
                completes. Source of truth for branches is the SNAPSHOT
                captured at enqueue (``dispatch_metadata.branches``);
                later sink edits don't retroactively change what was
                possible on THIS delivery. */}
            <SubflowSection
              detail={d}
              canExecuteAction={canExecuteAction}
              actionError={actionError}
              firingBranchId={
                executeAction.isPending
                  ? executeAction.variables ?? null
                  : null
              }
              onFire={(branchId) => executeAction.mutate(branchId)}
            />

            {/* Last response */}
            {d.last_response && Object.keys(d.last_response).length > 0 && (
              <CollapsibleSection title="Last response">
                <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-[11px]">
                  {JSON.stringify(d.last_response, null, 2)}
                </pre>
              </CollapsibleSection>
            )}

            {/* Dispatch metadata */}
            <CollapsibleSection title="Dispatch metadata">
              <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-[11px]">
                {JSON.stringify(d.dispatch_metadata, null, 2)}
              </pre>
            </CollapsibleSection>

            {/* Headers */}
            {Object.keys(d.headers).length > 0 && (
              <CollapsibleSection title="Headers (pre-resolved)">
                <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-[11px]">
                  {JSON.stringify(_redactSensitive(d.headers), null, 2)}
                </pre>
              </CollapsibleSection>
            )}

            {/* Payload preview */}
            {d.payload_preview && (
              <CollapsibleSection
                title={`Payload preview (${d.payload_size_bytes ?? "?"} bytes)`}
              >
                <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-[11px]">
                  {d.payload_preview}
                </pre>
              </CollapsibleSection>
            )}

            {/* Actions */}
            {canExecute && (
              <div className="sticky bottom-0 -mx-2 mt-2 flex justify-end gap-2 border-t border-border/60 bg-background p-3">
                {(d.status === "pending" || d.status === "in_flight") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={cancel.isPending}
                    onClick={() => {
                      if (confirm("Cancel this delivery (mark dead-letter)?")) {
                        cancel.mutate();
                      }
                    }}
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Cancel
                  </Button>
                )}
                <Button
                  size="sm"
                  disabled={retry.isPending || d.status === "in_flight"}
                  onClick={() => retry.mutate()}
                >
                  <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Retry now
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


// ---------------------------------------------------------------------------
// Small inline helpers
// ---------------------------------------------------------------------------


function DetailGrid({
  rows,
}: {
  rows: (readonly [string, React.ReactNode] | null)[];
}) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-xs">
      {rows.filter((r): r is readonly [string, React.ReactNode] => r !== null).map(
        ([k, v]) => (
          <React.Fragment key={k}>
            <div className="text-muted-foreground">{k}</div>
            <div className="break-all">{v}</div>
          </React.Fragment>
        ),
      )}
    </div>
  );
}


function CollapsibleSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded border border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium hover:bg-muted/50"
      >
        <span>{title}</span>
        <span className="text-muted-foreground">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="border-t border-border/60 p-2">{children}</div>}
    </div>
  );
}


/** ADR-0008 PR3b2 — sub-flow section of the delivery drawer.
 *
 *  Three blocks, all rendered against the SNAPSHOT taken at enqueue
 *  (``dispatch_metadata.branches``) so the operator sees what was
 *  possible on THIS delivery — later sink edits don't retroactively
 *  alter the picture.
 *
 *  1. **Outcome chip** — the terminal classification (``outcome_branch``)
 *     with a tint matching the branch (succeeded=green, dead_letter=red,
 *     custom=violet). Shown only post-classification.
 *
 *  2. **Branch grid** — one row per snapshotted SinkBranch, each
 *     showing trigger mode + linked-workflow status. For branches with
 *     ``trigger ∈ {manual, both}`` AND a linked workflow, the admin
 *     gets a "Fire" button. Auto-only branches show a read-only chip.
 *     Unlinked hook points are dimmed.
 *
 *  3. **Fires timeline** — chronological log of every spawn (auto + manual).
 *     Each row links to the sub-flow's standalone editor and (if a sub
 *     execution was actually started) to its trace.
 */
function SubflowSection({
  detail,
  canExecuteAction,
  actionError,
  firingBranchId,
  onFire,
}: {
  detail: import("@/lib/api-deliveries").DeliveryDetail;
  canExecuteAction: boolean;
  actionError: string | null;
  firingBranchId: string | null;
  onFire: (branchId: string) => void;
}) {
  // Snapshot lives at dispatch_metadata.branches (PR3a.3). May be
  // absent on legacy deliveries created before the snapshot landed —
  // skip the whole section in that case to keep the drawer clean.
  const branchesRaw = detail.dispatch_metadata?.branches;
  const branches: DeliveryBranchSnapshot[] = Array.isArray(branchesRaw)
    ? (branchesRaw as DeliveryBranchSnapshot[])
    : [];
  const fires: SubflowFire[] = detail.subflow_fires ?? [];

  if (branches.length === 0 && fires.length === 0 && !detail.outcome_branch) {
    return null;
  }

  // Outcome tint + label. Pure helpers extracted to
  // deliverySubflowView.ts (tested).
  const outcome = detail.outcome_branch;
  const tone = outcomeTone(outcome);

  return (
    <section className="rounded border border-border/60">
      <header className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
        <Zap className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Sub-flows
        </h3>
        {outcome && (
          <span
            className={
              "ml-auto inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider " +
              tone
            }
            title="Outcome branch classified by the drain worker"
          >
            outcome: {outcome}
          </span>
        )}
      </header>

      <div className="flex flex-col gap-3 p-3">
        {/* Action error banner — surfaced inline so a failed fire
            (e.g. 400 branch_not_manually_triggerable) is visible in
            context, not lost in a console log. */}
        {actionError && (
          <Alert tone="danger" title="Couldn't fire sub-flow">
            <span className="text-xs">{actionError}</span>
          </Alert>
        )}

        {/* Branch grid */}
        {branches.length > 0 && (
          <div className="flex flex-col divide-y divide-border/40">
            {branches.map((b) => {
              const canFireManually = canFireBranch(b, canExecuteAction);
              const isFiring = firingBranchId === b.id;
              return (
                <div
                  key={b.id}
                  className="flex items-center gap-2 py-1.5 text-xs"
                >
                  <span className="font-mono text-foreground">{b.id}</span>
                  {b.label && b.label !== b.id && (
                    <span className="text-muted-foreground">— {b.label}</span>
                  )}
                  <TriggerChip trigger={b.trigger} />
                  {b.linked_workflow_id ? (
                    <a
                      href={`/workflows/${b.linked_workflow_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                      title="Open the linked sub-flow in a new tab"
                    >
                      <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                      sub-flow
                    </a>
                  ) : (
                    <span className="text-[10px] italic text-muted-foreground">
                      no link
                    </span>
                  )}
                  {canFireManually && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-6 gap-1 px-2 text-[11px]"
                      disabled={isFiring}
                      onClick={() => onFire(b.id)}
                      title={`Fire the sub-flow on branch "${b.id}"`}
                    >
                      {isFiring ? (
                        <Loader2
                          className="h-3 w-3 animate-spin"
                          strokeWidth={1.5}
                        />
                      ) : (
                        <Hand className="h-3 w-3" strokeWidth={1.5} />
                      )}
                      Fire
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Fires timeline */}
        {fires.length > 0 ? (
          <div>
            <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Fires ({fires.length})
            </h4>
            <ol className="flex flex-col gap-1.5 border-l border-border/40 pl-3">
              {fires.map((f, i) => (
                <li
                  key={`${f.triggered_at}-${i}`}
                  className="flex items-center gap-2 text-[11px]"
                >
                  <span className="font-mono text-muted-foreground">
                    {new Date(f.triggered_at).toLocaleTimeString()}
                  </span>
                  <span
                    className={
                      "inline-flex items-center rounded px-1 py-px text-[9px] font-medium uppercase tracking-wider " +
                      (f.triggered_by === "manual"
                        ? "border border-amber-400/40 bg-amber-400/10 text-amber-300"
                        : "border border-sky-400/40 bg-sky-400/10 text-sky-300")
                    }
                  >
                    {f.triggered_by}
                  </span>
                  <span className="font-mono text-foreground">{f.branch_id}</span>
                  {f.actor && f.triggered_by === "manual" && (
                    <span
                      className="text-muted-foreground"
                      title="Operator who fired this action"
                    >
                      by {f.actor}
                    </span>
                  )}
                  {f.sub_execution_id ? (
                    <Link
                      to={`/trace/execution/${f.sub_execution_id}`}
                      className="ml-auto inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                      title="Open the spawned sub-execution in the trace explorer"
                    >
                      <GitBranch className="h-3 w-3" strokeWidth={1.5} />
                      <span className="font-mono text-[10px]">
                        {f.sub_execution_id.slice(0, 8)}…
                      </span>
                    </Link>
                  ) : (
                    <span
                      className="ml-auto text-[10px] italic text-muted-foreground"
                      title="No sub-execution id was recorded (DBOS disabled, dev / test)"
                    >
                      no exec id
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p className="text-[11px] italic text-muted-foreground">
            No sub-flow fires yet.
          </p>
        )}
      </div>
    </section>
  );
}


/** Small chip for the SinkBranch.trigger value (auto / manual / both). */
function TriggerChip({ trigger }: { trigger: "auto" | "manual" | "both" }) {
  const tone =
    trigger === "auto"
      ? "border-sky-400/40 bg-sky-400/10 text-sky-300"
      : trigger === "manual"
        ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
        : "border-violet-400/40 bg-violet-400/10 text-violet-300";
  return (
    <span
      className={
        "inline-flex items-center rounded border px-1 py-px text-[9px] font-medium uppercase tracking-wider " +
        tone
      }
      title={
        trigger === "auto"
          ? "Auto-fired on outcome classification"
          : trigger === "manual"
            ? "Operator-triggered only"
            : "Auto-fired + operator can re-trigger manually"
      }
    >
      {trigger}
    </span>
  );
}


/** Redact common bearer/api-key headers in the drawer view. */
function _redactSensitive(headers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(headers)) {
    const lower = k.toLowerCase();
    if (
      lower === "authorization" ||
      lower === "x-api-key" ||
      lower.includes("token") ||
      lower.includes("secret")
    ) {
      out[k] = "***";
    } else {
      out[k] = v;
    }
  }
  return out;
}
